import { Injectable } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { ArtifactAssemblyTypes } from 'src/app/developer/models/artifact';
import { TransportService } from 'src/app/transport/services/transport.service';
import { CurrentUserService } from 'src/app/user/services';

interface NavigationArguments {
  commands: any[];
  extras?: NavigationExtras;
}

interface EmbeddedItemInfo {
  area: string;
  navigationArguments: (id: number) => NavigationArguments | Promise<NavigationArguments>;
  buttonLabel: string;
  permission: string;
  displayName: string;
}

@Injectable({
  providedIn: 'root'
})
export class EmbeddedItemsService {
  constructor(
    private router: Router,
    private currentUserService: CurrentUserService,
    private transportService: TransportService,
  ) { }

  private embeddedItemInfos: EmbeddedItemInfo[] = [
    {
      area: ArtifactAssemblyTypes.TypeTransportRetrieve,
      navigationArguments: id => ({
        commands: [
          'transport-locations/incoming/details',
          { transportConfigurationsIds: id }
        ]
      }),
      buttonLabel: 'Go to ingoing transport location',
      permission: 'Transport_View_IncomingLocations_Details',
      displayName: 'Transport retrieve'
    },
    {
      area: ArtifactAssemblyTypes.TypeTransportSend,
      navigationArguments: id => ({
        commands: [
          'transport-locations/outgoing/details',
          { locationIds: [id] }
        ]
      }),
      buttonLabel: 'Go to outgoing transport location',
      permission: 'Transport_View_OutgoingLocation_Details',
      displayName: 'Transport send'
    }
  ];

  private getEmbeddedItemInfo(embeddedTypeArea: string): EmbeddedItemInfo {
    return this.embeddedItemInfos.find(e => e.area.localeCompare(embeddedTypeArea, undefined, { sensitivity: 'base' }) === 0);
  }

  getDisplayNameForEmbeddedItem(embeddedTypeArea: string) {
    return this.getEmbeddedItemInfo(embeddedTypeArea)?.displayName ?? 'N/A';
  }

  getButtonLabelForEmbeddedItem(embeddedTypeArea: string): string {
    return this.getEmbeddedItemInfo(embeddedTypeArea)?.buttonLabel ?? 'N/A';
  }

  canNavigateToEmbeddedItem(embeddedTypeArea: string): boolean {
    const embeddedItemInfo = this.getEmbeddedItemInfo(embeddedTypeArea);
    return !!embeddedItemInfo && this.currentUserService.hasPermission(embeddedItemInfo.permission);
  }

  async navigateToEmbeddedItem(embeddedTypeArea: string, embeddedTypeId: number): Promise<void> {
    const embeddedItemInfo = this.getEmbeddedItemInfo(embeddedTypeArea);
    if (embeddedItemInfo) {
      const navigationArguments = await embeddedItemInfo.navigationArguments(embeddedTypeId);
      await this.router.navigate(navigationArguments.commands, navigationArguments.extras);
    }
  }
}
